import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Tabs, Collapse } from "antd";
import { CommentOutlined, FileTextOutlined, TeamOutlined } from "@ant-design/icons";
import styles from "./TacheDetailsPage.module.scss";
import LoadingPlaceholder from "../../components/utils/LoadingPlaceholder";
import Main from "../../components/layouts/Main";
import { useUserContext } from "../../contexts/UserContext";
import Breadcrumb from "../../components/utils/Breadcrumb";
import TacheEditForm from "../../components/tache/TacheEditForm";
import { StatutService } from "../../services/StatutService";
import TacheDetails from "../../components/tache/TacheDetails";
import TacheHero from "../../components/tache/TacheHero";
import Path from "../../components/utils/Path";
import { catchError } from "../../services/DaoService";
import { useSelector } from "@legendapp/state/react";
import { state$ } from "../../state/globalState";
import { tachesState } from "../../state/actions/tachesState";
import CommentaireFeed from "../../components/commentaire/CommentaireFeed";
import PieceJointeList from "../../components/piecejointe/PieceJointeList";
import LogTraces from "../../components/logtrace/LogTraces";
import ActivityFeed from "../../components/Activity/ActiviteFeed";
import MembreTacheByTacheList from "../../components/membretache/MembreTacheByTacheList";
import { TacheService } from "../../services/TacheService";
import { canEditTacheStep } from "../../helpers/permissions";

const TacheDetailsPage = () => {
  const { uid } = useParams();
  const [statuts, setStatuts] = useState([]);
  const { check, currentUser } = useUserContext();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const navigate = useNavigate();
  const { loading } = useSelector(() => state$.taches.get());
  const tache = useSelector(() => state$.taches.list.get().find(t => t.uid === uid));

  useEffect(() => {
    tachesState.getTache(uid);
    StatutService.getInstance().all().then(response => {
      setStatuts(response.data);
    }).catch(catchError);
  }, [uid]);

  function openEditModal() {
    setEditModalVisible(true);
  }

  const handleDelete = () => {
    tachesState.deleteTache(uid).then(() => {
      navigate("/tache");
    });
  };

  const handleUpdate = () => {
    setEditModalVisible(false);
  };

  const handleStepChange = (step) => {
    tachesState.changeTacheStep(uid, step.id);
  };

  const follow = () => {
    tachesState.followTache(tache.id, currentUser.id);
  };

  if (loading || !tache) return (
    <Main>
      <Breadcrumb
        parentLink="/tache"
        canSee={check("VIEW-TACHES")}
        parentLinkTitle="Liste des tâches"
        title="Chargement..."
      />
      <LoadingPlaceholder />
    </Main>
  );

  const canManageTache = currentUser?.id === tache?.user_id ||
    currentUser?.personnel?.id === tache?.responsable_id ||
    currentUser?.id === tache?.affaire?.user_id ||
    currentUser?.id === tache?.affaire?.responsable_id;
  const canEditStep = canEditTacheStep(tache, canManageTache);
  const canSeeLogs = canManageTache;
  const canManageMembers = canManageTache;
  const canComment = !!tache?.current_member || !!tache?.affaire?.current_membre || canManageTache;
  const canAttach = canComment;

  const tabItems = [
    {
      key: 'commentaires',
      label: <span><CommentOutlined /> Commentaires</span>,
      children: <CommentaireFeed parentId={tache.id} parentType={TacheService.CLASS_NAME} canCreate={canComment} />,
    },
    canSeeLogs && {
      key: 'logs',
      label: <span><FileTextOutlined /> Logs</span>,
      children: <LogTraces target="tache" parentId={tache.id} />,
    },
  ].filter(Boolean);

  return (
    <Main>
      <Breadcrumb
        parentLink={tache?.affaire ? `/affaire/${tache.affaire.uid}` : '/tache'}
        canSee={tache?.affaire ? check('VIEW-AFFAIRES') : check('VIEW-TACHES')}
        parentLinkTitle={tache?.affaire ? tache.affaire.code : 'Liste des tâches'}
        title={tache?.nom ?? 'En chargement...'}
      />

      <TacheHero
        tache={tache}
        loading={loading}
        canManage={canManageTache}
        onEdit={openEditModal}
        onDelete={handleDelete}
        onRefresh={() => tachesState.getTache(uid)}
        onFollow={follow}
      />

      <div className={styles.pathContainer}>
        <Path
          currentStep={tache?.statut?.code}
          steps={statuts}
          onCurrentPathChange={handleStepChange}
          readOnly={!canEditStep}
        />
      </div>

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={16}>
          <div className={styles.colStack}>
            <Collapse
              className={styles.card}
              defaultActiveKey={['observateurs']}
              items={[{
                key: 'observateurs',
                label: (
                  <span className={styles.collapseLabel}>
                    <TeamOutlined />
                    Observateurs
                  </span>
                ),
                children: <MembreTacheByTacheList tache={tache} isReadOnly={!!tache?.statut?.resolu} canManage={canManageMembers} />,
              }]}
            />

            <Collapse
              className={styles.card}
              defaultActiveKey={['documents']}
              items={[{
                key: 'documents',
                label: (
                  <span className={styles.collapseLabel}>
                    <FileTextOutlined />
                    Documents
                  </span>
                ),
                children: (
                  <PieceJointeList
                    parentId={tache.id}
                    parentType={TacheService.CLASS_NAME}
                    canAdd={canAttach}
                    canRemove={canManageTache}
                    canSee
                  />
                ),
              }]}
            />

            <div className={styles.tabsCard}>
              <Tabs
                defaultActiveKey="commentaires"
                items={tabItems}
              />
            </div>
          </div>
        </Col>

        <Col xs={24} lg={8}>
          <div className={styles.colStack}>
            <TacheDetails tache={tache} />
            <ActivityFeed parentId={tache.id} parentType={TacheService.CLASS_NAME} />
          </div>
        </Col>
      </Row>

      {editModalVisible && tache && (
        <TacheEditForm
          visible={editModalVisible}
          uid={tache.uid}
          onUpdate={handleUpdate}
          onCancel={() => setEditModalVisible(false)}
        />
      )}
    </Main>
  );
};

export default TacheDetailsPage;
